import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiRequest } from '../hooks/useApi.js'
import { useAnnouncer } from '../contexts/AnnouncerContext.jsx'
import Scoreboard from '../components/Scoreboard.jsx'
import ViraInfo from '../components/ViraInfo.jsx'
import PlayerHand from '../components/PlayerHand.jsx'
import GameHistory from '../components/GameHistory.jsx'
import ActionButtons from '../components/ActionButtons.jsx'
import BotStatus from '../components/BotStatus.jsx'
import ResponseDialog from '../components/ResponseDialog.jsx'
import HandSummaryDialog from '../components/HandSummaryDialog.jsx'
import MatchOverDialog from '../components/MatchOverDialog.jsx'

export default function GameScreen() {
  const navigate = useNavigate()
  const { announce, alertSR } = useAnnouncer()
  const [gameState, setGameState] = useState(null)
  const [loading, setLoading] = useState(true)

  const announceHistoryEntry = useCallback((msg) => {
    alertSR(msg)
  }, [alertSR])

  const loadGameState = useCallback(async () => {
    try {
      const data = await apiRequest('GET', '/game/state')
      setGameState(data)
      announce('Partida carregada.')
      return data
    } catch (err) {
      if (err.message.includes('404') || err.message.includes('No active game')) {
        announce('Nenhuma partida em andamento.')
        navigate('/menu')
      } else {
        alertSR(`Erro ao carregar partida: ${err.message}`)
      }
      return null
    }
  }, [announce, alertSR, navigate])

  useEffect(() => {
    let cancelled = false
    async function init() {
      setLoading(true)
      if (!cancelled) {
        await loadGameState()
        setLoading(false)
      }
    }
    init()
    return () => { cancelled = true }
  }, [loadGameState])

  async function playCard(index) {
    try {
      const data = await apiRequest('POST', '/game/play', { card_index: index })
      setGameState(data)
    } catch (err) {
      alertSR(`Erro ao jogar carta: ${err.message}`)
    }
  }

  async function requestRaise() {
    try {
      const data = await apiRequest('POST', '/game/raise')
      setGameState(data)
    } catch (err) {
      alertSR(`Erro ao pedir truco: ${err.message}`)
    }
  }

  async function respondToRaise(action) {
    try {
      const data = await apiRequest('POST', '/game/respond', { action })
      setGameState(data)
    } catch (err) {
      alertSR(`Erro ao responder: ${err.message}`)
    }
  }

  async function nextHand() {
    try {
      const data = await apiRequest('POST', '/game/next-hand')
      setGameState(data)
      announce('Nova mão distribuída.')
    } catch (err) {
      alertSR(`Erro ao iniciar próxima mão: ${err.message}`)
    }
  }

  async function surrender() {
    try {
      const data = await apiRequest('POST', '/game/surrender')
      setGameState(data)
    } catch (err) {
      alertSR(`Erro ao desistir: ${err.message}`)
    }
  }

  async function handleNewMatch() {
    try {
      const data = await apiRequest('POST', '/game/new', {
        difficulty: 'intermediario',
        tutorial_mode: false,
      })
      setGameState(data)
      announce('Nova partida iniciada.')
    } catch (err) {
      alertSR(`Erro ao criar partida: ${err.message}`)
    }
  }

  if (loading) {
    return (
      <section aria-labelledby="game-heading">
        <h2 id="game-heading">Partida em Andamento</h2>
        <p>Carregando partida...</p>
      </section>
    )
  }

  if (!gameState) {
    return (
      <section aria-labelledby="game-heading">
        <h2 id="game-heading">Partida em Andamento</h2>
        <p>Nenhuma partida ativa.</p>
        <button className="btn btn-secondary" onClick={() => navigate('/menu')}>
          Voltar ao menu
        </button>
      </section>
    )
  }

  const isBotTurn = gameState.phase === 'turno_computador'
  const isPlayerResponse = gameState.phase === 'resposta_jogador' && gameState.pending_raise
  const isHandOver = gameState.phase === 'fim_da_mao'
  const isMatchOver = gameState.match_over || gameState.phase === 'fim_da_partida'

  return (
    <section aria-labelledby="game-heading">
      <h2 id="game-heading" tabIndex="-1">Partida em Andamento</h2>

      <Scoreboard score={gameState.score} handValue={gameState.hand_value} />

      <ViraInfo vira={gameState.vira} manilhaRank={gameState.manilha_rank} />

      <PlayerHand
        cards={gameState.player_hand || []}
        disabled={isBotTurn || isPlayerResponse || isHandOver || isMatchOver}
        onPlayCard={playCard}
      />

      <div id="game-actions" aria-label="Ações do jogo">
        <ActionButtons
          actions={gameState.available_actions || []}
          handValue={gameState.hand_value}
          onRaise={requestRaise}
          onSurrender={surrender}
          onStartHand={nextHand}
        />
      </div>

      <BotStatus visible={isBotTurn} />

      <GameHistory
        history={gameState.history || []}
        onNewEntry={announceHistoryEntry}
      />

      <ResponseDialog
        pendingRaise={isPlayerResponse ? gameState.pending_raise : null}
        playerHand={isPlayerResponse ? gameState.player_hand : null}
        manilhaRank={isPlayerResponse ? gameState.manilha_rank : null}
        onAccept={() => respondToRaise('accept')}
        onEscalate={() => respondToRaise('escalate')}
        onDecline={() => respondToRaise('decline')}
      />

      <HandSummaryDialog
        summary={isHandOver ? gameState.game_state?.hand_over_summary : null}
        score={gameState.score}
        onNextHand={nextHand}
      />

      <MatchOverDialog
        gameState={gameState}
        onNewMatch={handleNewMatch}
        onBackMenu={() => {}}
      />
    </section>
  )
}
